import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import GaugeMeter from '../../components/ui/GaugeMeter';
import { sessionService } from '../../services';
import exportService from '../../services/exportService';
import microSimService from '../../services/microSimService';
import MicroSimParticipantTab from '../../components/microsim/MicroSimParticipantTab';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getFeedbackRating, calculateAverageRating } from '../../utils/ratingUtils';
import {
  getMetricBandInfo,
  getARSInterpretation,
  getPCSInterpretation,
  getLIIInterpretation,
  BY_WHEN_LABELS
} from '../../utils/reportingConstants';
import { formatLocalDate } from '../../utils/dateUtils';

/**
 * Participant Details Page
 * Full page view showing participant information including all activity responses,
 * challenges, action plans, and feedback - matches the ParticipantReportsPage layout
 */
const ParticipantDetailsPage = () => {
  const { sessionId, participantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatDateWithTimezone } = usePreferences();
  const reportRef = useRef(null);

  // Read URL query params — `?tab=<id>` selects the initial tab,
  // `?only=preSession` restricts the page to show only the Pre-Session tab (used when a trainer
  // drills in from the Pre-Session analytics view and doesn't want the full participant report).
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get('tab') || 'overview';
  const preSessionOnly = searchParams.get('only') === 'preSession';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(preSessionOnly ? 'preSession' : initialTab);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [responseFilter, setResponseFilter] = useState('all');

  // Determine which panel we're in based on the URL path - go to session reports
  const getBackPath = () => {
    if (location.pathname.startsWith('/admin')) {
      return `/admin/sessions/${sessionId}/report`;
    } else if (location.pathname.startsWith('/trainer')) {
      return `/trainer/sessions/${sessionId}/report`;
    } else {
      return `/client/sessions/${sessionId}/report`;
    }
  };

  useEffect(() => {
    if (sessionId && participantId) {
      fetchParticipantDetails();
    }
  }, [sessionId, participantId]);

  // Micro simulation results — a separate product (D6), so a separate call. Failure or
  // no results just means no "Micro sims" tab.
  const [microSimReports, setMicroSimReports] = useState([]);
  useEffect(() => {
    if (!sessionId || !participantId || preSessionOnly) return undefined;
    let alive = true;
    microSimService.participantResults(sessionId, participantId)
      .then((res) => { if (alive) setMicroSimReports(res.reports || []); })
      .catch(() => { /* not available to this viewer */ });
    return () => { alive = false; };
  }, [sessionId, participantId, preSessionOnly]);

  const fetchParticipantDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sessionService.getParticipantDetails(sessionId, participantId);
      if (response.success) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to load participant details');
      }
    } catch (err) {
      console.error('Error fetching participant details:', err);
      setError(err.message || 'Failed to load participant details');
    } finally {
      setLoading(false);
    }
  };

  // Report tabs configuration - matches ParticipantReportsPage.
  // When `preSessionOnly` is active, `visibleTabs` below filters this down to just the Pre-Session tab.
  const reportTabs = [
    { id: 'overview', label: 'Overview', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    )},
    { id: 'participation', label: 'Participation', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    )},
    { id: 'challenges', label: 'Challenges', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    )},
    { id: 'actionPlans', label: 'Action Plans', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )},
    { id: 'responses', label: 'All Responses', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    )},
    { id: 'preSession', label: 'Pre-Session', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    )},
    { id: 'postSession', label: 'Post-Session', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
      </svg>
    )},
  ];

  const microSimTab = { id: 'microSims', label: 'Micro Sims', icon: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>
  )};
  const allTabs = microSimReports.length ? [...reportTabs, microSimTab] : reportTabs;
  const visibleTabs = preSessionOnly ? allTabs.filter(t => t.id === 'preSession') : allTabs;

  // Export participant data as PDF using backend's styled template
  const handleExportPDF = async () => {
    if (!data) return;
    setIsGeneratingPDF(true);

    try {
      const participantName = data.participant?.name || 'Participant';
      await exportService.exportParticipantDetailedReportPDF(
        sessionId,
        participantId,
        participantName
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Calculate overall stats
  const calculateOverallStats = () => {
    if (!data) return {
      sessionsAttended: 0,
      totalSessions: 0,
      engagementScore: 0,
      activitiesCompleted: 0,
      totalActivities: 0,
      launchedActivities: 0,
      totalChallenges: 0,
      totalActionPlans: 0,
      sessionRating: 0,
    };

    const totalSubsessions = data.subSessions?.length || 0;
    const totalActivities = data.subSessions?.reduce((sum, ss) => sum + (ss.activities?.length || 0), 0) || 0;
    const completedActivities = data.summary?.activitiesCompleted || 0;
    const launchedActivities = data.summary?.launchedActivities || totalActivities;

    // Calculate session rating from feedback responses
    let sessionRating = 0;
    const allResponses = data.subSessions?.flatMap(ss => ss.responses || []) || [];
    const feedbackResponses = allResponses.filter(r => {
      const type = (r.activityType || '').toLowerCase();
      return type === 'feedback' || type === 'rating';
    });

    if (feedbackResponses.length > 0) {
      const ratings = feedbackResponses.map(r => {
        const resp = r.response;
        if (resp?.rating) return resp.rating;
        if (resp?.emoji) return getFeedbackRating(resp.emoji);
        return 0;
      }).filter(r => r > 0);

      if (ratings.length > 0) {
        sessionRating = Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10;
      }
    }

    return {
      sessionsAttended: totalSubsessions,
      totalSessions: totalSubsessions,
      engagementScore: data.summary?.engagementScore || 0,
      activitiesCompleted: completedActivities,
      totalActivities: totalActivities,
      launchedActivities: launchedActivities,
      totalChallenges: data.summary?.totalChallenges || 0,
      totalActionPlans: data.summary?.totalActionPlans || 0,
      sessionRating: sessionRating,
    };
  };

  const overallStats = calculateOverallStats();
  const completionRate = overallStats.launchedActivities > 0
    ? Math.round((overallStats.activitiesCompleted / overallStats.launchedActivities) * 100)
    : 0;

  // Calculate participant metrics (ARS, PCS, LII)
  const calculateParticipantMetrics = () => {
    if (!data) return { hasData: false, ars: { value: 0 }, pcs: { value: 0 }, lii: { value: 0 } };

    const allResponses = data.subSessions?.flatMap(ss => ss.responses || []) || [];

    // Calculate ARS from action plans
    let totalARS = 0;
    let arsCount = 0;

    const urgencyWeights = {
      '7_days': 1.0,
      '15_days': 0.85,
      '30_days': 0.70,
      '60_days': 0.45,
      '90_days': 0.25,
      'not_sure': 0.15,
    };

    allResponses.forEach(r => {
      const type = (r.activityType || '').toLowerCase();
      if (type === 'actionplans' || type === 'actionplan') {
        const resp = r.response;
        const actions = resp?.actions || resp?.actionPlans || [];

        actions.forEach(action => {
          const byWhen = action?.byWhen || 'not_sure';
          const commitment = action?.commitment || 5;
          const urgencyWeight = urgencyWeights[byWhen] || 0.15;
          const commitmentFactor = commitment / 10;
          const ars = urgencyWeight * commitmentFactor;
          totalARS += ars;
          arsCount++;
        });
      }
    });

    const arsValue = arsCount > 0 ? Math.round((totalARS / arsCount) * 100) / 100 : 0;

    // Calculate PCS from feedback Q3 (confidenceToApply)
    let totalPCS = 0;
    let pcsCount = 0;

    allResponses.forEach(r => {
      const type = (r.activityType || '').toLowerCase();
      if (type === 'feedback') {
        const resp = r.response;
        if (resp?.confidenceToApply) {
          totalPCS += resp.confidenceToApply;
          pcsCount++;
        }
      }
    });

    const pcsValue = pcsCount > 0 ? Math.round((totalPCS / pcsCount) * 10) / 10 : 0;

    // Calculate LII from feedback Q4 (learningAdvocacy)
    let totalLII = 0;
    let liiCount = 0;

    allResponses.forEach(r => {
      const type = (r.activityType || '').toLowerCase();
      if (type === 'feedback') {
        const resp = r.response;
        if (resp?.learningAdvocacy) {
          totalLII += resp.learningAdvocacy;
          liiCount++;
        }
      }
    });

    const liiValue = liiCount > 0 ? Math.round((totalLII / liiCount) * 10) / 10 : 0;

    return {
      hasData: arsValue > 0 || pcsValue > 0 || liiValue > 0,
      ars: {
        value: arsValue,
        band: arsValue > 0 ? getMetricBandInfo('ars', arsValue) : null,
        interpretation: arsValue > 0 ? getARSInterpretation(arsValue, 'participant') : null,
      },
      pcs: {
        value: pcsValue,
        band: pcsValue > 0 ? getMetricBandInfo('pcs', pcsValue) : null,
        interpretation: pcsValue > 0 ? getPCSInterpretation(pcsValue, 'participant') : null,
      },
      lii: {
        value: liiValue,
        band: liiValue > 0 ? getMetricBandInfo('lii', liiValue) : null,
        interpretation: liiValue > 0 ? getLIIInterpretation(liiValue, 'participant') : null,
      },
    };
  };

  const participantMetrics = calculateParticipantMetrics();

  // Build session participation data
  const buildSessionParticipation = () => {
    if (!data?.subSessions) return [];

    return data.subSessions.map((ss, idx) => {
      const activities = ss.activities || [];
      const responses = ss.responses || [];

      // Build activity list with completion status
      const activityList = activities.map(activity => {
        const hasResponse = responses.some(r =>
          r.activityId === activity._id ||
          r.activityId === activity.id ||
          (r.activityType === activity.type && r.activityTitle === activity.title)
        );
        const isLaunched = activity.launchedAt ||
          ['active', 'launched', 'completed', 'ended', 'in_progress'].includes(activity.status);

        let status = 'not_launched';
        if (hasResponse) {
          status = 'completed';
        } else if (isLaunched) {
          status = 'not_attended';
        }

        return {
          name: activity.title || activity.type?.replace('_', ' '),
          type: activity.type,
          status: status,
        };
      });

      // Count challenges and action plans
      const challengesCount = responses.filter(r =>
        (r.activityType || '').toLowerCase() === 'challenges'
      ).length;

      const actionPlansCount = responses.filter(r => {
        const type = (r.activityType || '').toLowerCase();
        return type === 'actionplans' || type === 'actionplan';
      }).length;

      // Calculate per-subsession metrics
      let ssARS = 0, arsCount = 0;
      let ssPCS = 0, pcsCount = 0;
      let ssLII = 0, liiCount = 0;

      const urgencyWeights = {
        '7_days': 1.0, '15_days': 0.85, '30_days': 0.70,
        '60_days': 0.45, '90_days': 0.25, 'not_sure': 0.15,
      };

      responses.forEach(r => {
        const type = (r.activityType || '').toLowerCase();
        const resp = r.response;

        if (type === 'actionplans' || type === 'actionplan') {
          const actions = resp?.actions || resp?.actionPlans || [];
          actions.forEach(action => {
            const byWhen = action?.byWhen || 'not_sure';
            const commitment = action?.commitment || 5;
            const urgencyWeight = urgencyWeights[byWhen] || 0.15;
            const commitmentFactor = commitment / 10;
            ssARS += urgencyWeight * commitmentFactor;
            arsCount++;
          });
        }

        if (type === 'feedback') {
          if (resp?.confidenceToApply) {
            ssPCS += resp.confidenceToApply;
            pcsCount++;
          }
          if (resp?.learningAdvocacy) {
            ssLII += resp.learningAdvocacy;
            liiCount++;
          }
        }
      });

      // Key takeaways from word cloud or feedback
      const keyTakeaways = [];
      responses.forEach(r => {
        const type = (r.activityType || '').toLowerCase();
        const resp = r.response;

        if (type === 'wordcloud' || type === 'word_cloud') {
          if (resp?.word) keyTakeaways.push(resp.word);
          if (resp?.words) keyTakeaways.push(...resp.words);
        }
      });

      return {
        day: idx + 1,
        topic: ss.name || `Session ${idx + 1}`,
        activities: activityList,
        metrics: {
          activitiesCount: `${responses.length}/${activities.filter(a =>
            a.launchedAt || ['active', 'launched', 'completed', 'ended'].includes(a.status)
          ).length || activities.length}`,
          challengesCount: challengesCount,
          actionPlansCount: actionPlansCount,
          ars: {
            value: arsCount > 0 ? Math.round((ssARS / arsCount) * 100) / 100 : 0,
            band: arsCount > 0 ? getMetricBandInfo('ars', ssARS / arsCount) : null,
          },
          pcs: {
            value: pcsCount > 0 ? Math.round((ssPCS / pcsCount) * 10) / 10 : 0,
            band: pcsCount > 0 ? getMetricBandInfo('pcs', ssPCS / pcsCount) : null,
          },
          lii: {
            value: liiCount > 0 ? Math.round((ssLII / liiCount) * 10) / 10 : 0,
            band: liiCount > 0 ? getMetricBandInfo('lii', ssLII / liiCount) : null,
          },
        },
        keyTakeaways: keyTakeaways.slice(0, 5),
      };
    });
  };

  const sessionParticipation = buildSessionParticipation();

  // Extract challenges
  const extractChallenges = () => {
    if (!data?.subSessions) return [];

    const challenges = [];
    data.subSessions.forEach(ss => {
      (ss.responses || []).forEach(r => {
        const type = (r.activityType || '').toLowerCase();
        if (type === 'challenges') {
          const resp = r.response;
          let items = [];

          if (resp?.challenges && Array.isArray(resp.challenges)) {
            items = resp.challenges.filter(c => c && c.trim());
          } else if (resp?.challenge || resp?.text) {
            items = [resp.challenge || resp.text];
          }

          if (items.length > 0) {
            challenges.push({
              topic: ss.name || 'Session',
              activityTitle: r.activityTitle,
              challenges: items,
            });
          }
        }
      });
    });
    return challenges;
  };

  // Extract action plans
  const extractActionPlans = () => {
    if (!data?.subSessions) return [];

    const actionPlans = [];
    data.subSessions.forEach(ss => {
      (ss.responses || []).forEach(r => {
        const type = (r.activityType || '').toLowerCase();
        if (type === 'actionplans' || type === 'actionplan') {
          const resp = r.response;
          let items = [];

          if (resp?.actions && Array.isArray(resp.actions)) {
            items = resp.actions.map(a => ({
              text: a.text || a.action || a.plan || a,
              byWhen: a.byWhen,
              commitment: a.commitment,
            }));
          } else if (resp?.actionPlans && Array.isArray(resp.actionPlans)) {
            items = resp.actionPlans.map(a => ({
              text: typeof a === 'string' ? a : (a.text || a.action || a.plan),
              byWhen: a?.byWhen,
              commitment: a?.commitment,
            }));
          } else if (resp?.action || resp?.text || resp?.plan) {
            items = [{
              text: resp.action || resp.text || resp.plan,
              byWhen: resp.byWhen,
              commitment: resp.commitment,
            }];
          }

          if (items.length > 0) {
            actionPlans.push({
              topic: ss.name || 'Session',
              activityTitle: r.activityTitle,
              actions: items,
            });
          }
        }
      });
    });
    return actionPlans;
  };

  const personalChallenges = extractChallenges();
  const personalActionPlans = extractActionPlans();

  // Get all responses for the All Responses tab
  const getAllResponses = () => {
    if (!data?.subSessions) return [];

    const responses = [];
    data.subSessions.forEach(ss => {
      (ss.responses || []).forEach(r => {
        // Find the matching activity to get config (for poll options, etc.)
        const activity = (ss.activities || []).find(a =>
          a._id === r.activityId ||
          a._id?.toString() === r.activityId?.toString() ||
          (a.type === r.activityType && a.title === r.activityTitle)
        );
        responses.push({
          ...r,
          subSessionName: ss.name || 'Session',
          activityConfig: activity?.config,
        });
      });
    });
    return responses;
  };

  const allResponses = getAllResponses();

  // Format response for display
  const formatResponse = (response, activityType, activityConfig = null) => {
    if (!response) return 'No response';
    if (typeof response === 'string') return response;

    const type = activityType?.toLowerCase();

    // Helper to get option text from index
    const getOptionText = (index, options) => {
      if (!options || !Array.isArray(options) || index < 0 || index >= options.length) {
        return `Option ${index + 1}`;
      }
      const opt = options[index];
      return typeof opt === 'string' ? opt : (opt?.text || opt?.label || `Option ${index + 1}`);
    };

    if (type === 'poll') {
      const options = activityConfig?.options || [];

      // Check for selectedLabel first (the actual option text)
      if (response.selectedLabel) {
        return response.selectedLabel;
      }
      if (response.selectedLabels && Array.isArray(response.selectedLabels)) {
        return response.selectedLabels.join(', ');
      }
      // Handle typed responses with value - look up actual option text
      if (response.type === 'single' && response.value !== undefined && response.value !== null) {
        return getOptionText(response.value, options);
      }
      if (response.type === 'multiple' && Array.isArray(response.value)) {
        return response.value.map(v => getOptionText(v, options)).join(', ');
      }
      // Handle other formats: optionIndex, single, etc.
      if (response.optionIndex !== undefined && response.optionIndex !== null) {
        return getOptionText(response.optionIndex, options);
      }
      if (response.single !== undefined && response.single !== null) {
        return getOptionText(response.single, options);
      }
      if (response.multiple && Array.isArray(response.multiple)) {
        return response.multiple.map(v => getOptionText(v, options)).join(', ');
      }
      // Check if selectedOption is text or index
      if (response.selectedOption !== undefined) {
        if (typeof response.selectedOption === 'string' && isNaN(response.selectedOption)) {
          return response.selectedOption;
        }
        const idx = parseInt(response.selectedOption, 10);
        if (!isNaN(idx)) {
          return getOptionText(idx, options);
        }
      }
      return response.option || 'N/A';
    }

    if (type === 'wordcloud' || type === 'word_cloud') {
      if (response.word) return response.word;
      if (response.words && Array.isArray(response.words)) return response.words.join(', ');
      return response.text || 'N/A';
    }

    if (type === 'feedback') {
      const parts = [];
      if (response.rating) parts.push(`Rating: ${response.rating}/5`);
      if (response.emoji) parts.push(response.emoji);
      if (response.topicRelevance) parts.push(`Topic: ${response.topicRelevance}/5`);
      if (response.trainerEffectiveness) parts.push(`Trainer: ${response.trainerEffectiveness}/5`);
      if (response.confidenceToApply) parts.push(`Confidence: ${response.confidenceToApply}/5`);
      if (response.learningAdvocacy) parts.push(`Advocacy: ${response.learningAdvocacy}/5`);
      if (response.challengeResolution) parts.push(`Resolution: ${response.challengeResolution}/5`);
      if (response.comment || response.feedback) parts.push(response.comment || response.feedback);
      return parts.length > 0 ? parts.join(' | ') : 'Feedback submitted';
    }

    if (type === 'quiz') {
      const questions = activityConfig?.questions || [];
      const answers = response.answers || response.results?.answers;
      const questionDetails = response.questionDetails;

      // Helper to get answer text from question options
      const getAnswerText = (questionIndex, answerIndex) => {
        if (questions[questionIndex]) {
          const q = questions[questionIndex];
          const opts = q.options || q.choices || [];
          if (opts[answerIndex]) {
            const opt = opts[answerIndex];
            return typeof opt === 'string' ? opt : (opt.text || opt.label || `Option ${answerIndex + 1}`);
          }
        }
        return `Option ${answerIndex + 1}`;
      };

      // If we have questionDetails with the actual answers
      if (questionDetails && Array.isArray(questionDetails)) {
        const answerTexts = questionDetails.map((qd, idx) => {
          const isCorrect = qd.isCorrect ? '✓' : '✗';

          // Get answer text from questionDetails options array using userAnswer index
          let answerText = 'No answer';
          if (qd.userAnswer !== null && qd.userAnswer !== undefined) {
            const answerIndex = parseInt(qd.userAnswer, 10);
            if (!isNaN(answerIndex)) {
              // First try to get from qd.options (stored with the response)
              if (qd.options && Array.isArray(qd.options) && qd.options[answerIndex]) {
                const opt = qd.options[answerIndex];
                answerText = typeof opt === 'string' ? opt : (opt.text || opt.label || `Option ${answerIndex + 1}`);
              } else {
                // Fall back to activity config questions
                answerText = getAnswerText(idx, answerIndex);
              }
            }
          }

          return `${isCorrect} ${answerText}`;
        });
        return answerTexts;
      }

      // Handle answers as object { 0: answerIndex, 1: answerIndex, ... }
      if (answers && typeof answers === 'object' && !Array.isArray(answers)) {
        const answerTexts = Object.entries(answers).map(([qIdx, aIdx]) => {
          return getAnswerText(parseInt(qIdx, 10), aIdx);
        });
        return answerTexts.length > 0 ? answerTexts : 'Quiz completed';
      }

      // Handle answers as array
      if (answers && Array.isArray(answers)) {
        const answerTexts = answers.map((ans, idx) => {
          if (typeof ans === 'object') {
            return ans.selectedLabel || ans.answer || getAnswerText(idx, ans.answerIndex || ans.value);
          }
          return getAnswerText(idx, ans);
        });
        return answerTexts.length > 0 ? answerTexts : 'Quiz completed';
      }

      // Show score if available
      if (response.results?.percentage !== undefined || response.score !== undefined) {
        const score = response.results?.percentage ?? response.score;
        return `Score: ${score}%`;
      }

      return response.selectedOption || response.selectedLabel || response.answer || 'Quiz completed';
    }

    if (type === 'qa' || type === 'qna' || type === 'openquestion' || type === 'open_question') {
      return response.response || response.text || response.answer || response.question || 'N/A';
    }

    if (type === 'challenges') {
      if (Array.isArray(response.challenges)) {
        return response.challenges.filter(c => c && c.trim());
      }
      return response.challenge || response.text || 'N/A';
    }

    if (type === 'actionplans' || type === 'actionplan') {
      if (Array.isArray(response.actions)) {
        return response.actions.map(a => a.text || a.action || a.plan || a);
      }
      if (Array.isArray(response.actionPlans)) {
        return response.actionPlans.map(a => typeof a === 'string' ? a : (a.text || a.action || a.plan));
      }
      return response.action || response.plan || response.text || 'N/A';
    }

    return typeof response === 'object' ? 'Response recorded' : String(response);
  };

  // Filter responses
  const filteredResponses = responseFilter === 'all'
    ? allResponses
    : allResponses.filter(r => {
        const type = (r.activityType || '').toLowerCase();
        // Handle variations of activity types
        if (responseFilter === 'wordcloud') {
          return type === 'wordcloud' || type === 'word_cloud';
        }
        if (responseFilter === 'qa') {
          return type === 'qa' || type === 'qna' || type === 'openquestion' || type === 'open_question';
        }
        if (responseFilter === 'actionplans') {
          return type === 'actionplans' || type === 'actionplan';
        }
        return type === responseFilter;
      });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <span className="ml-4 text-lg text-neutral-600">Loading participant details...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-24">
          <svg className="w-16 h-16 text-neutral-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-lg text-neutral-600 mb-4">{error || 'Failed to load participant details'}</p>
          <Button variant="secondary" onClick={() => navigate(getBackPath())}>
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const participant = data.participant || {};

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(getBackPath())}
              className="flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="hidden sm:inline">Back to Report</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
          {!preSessionOnly && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportPDF}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin"></span>
                <span className="hidden sm:inline">Exporting...</span>
                <span className="sm:hidden text-xs">PDF...</span>
              </span>
            ) : (
              <span className="flex items-center gap-1 sm:gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="hidden sm:inline">Export PDF</span>
                <span className="sm:hidden">PDF</span>
              </span>
            )}
          </Button>
          )}
        </div>

        {/* Report Tabs */}
        <div className="mb-4 sm:mb-6 overflow-x-auto scrollbar-hide -mx-3 sm:-mx-0 px-3 sm:px-0">
          <div className="flex gap-1 sm:gap-2 min-w-max pb-2 border-b border-neutral-200">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <span className="[&>svg]:w-3.5 [&>svg]:h-3.5 sm:[&>svg]:w-4 sm:[&>svg]:h-4">{tab.icon}</span>
                <span>{tab.id === 'actionPlans' ? 'Action' : tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Participant Info Header */}
        <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-5 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 truncate">{participant.name || 'Participant'}</h2>
              <p className="text-sm sm:text-base text-neutral-500 mt-1 truncate">{data.session?.name}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-neutral-600">
                <span className="flex items-center gap-1.5 min-w-0">
                  <svg className="w-4 h-4 flex-shrink-0 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="truncate">{participant.email || 'N/A'}</span>
                </span>
                <span className="flex items-center gap-1.5 min-w-0">
                  <svg className="w-4 h-4 flex-shrink-0 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="truncate">{participant.organization || 'N/A'}</span>
                </span>
              </div>
            </div>
            <Badge variant={participant.status === 'joined' ? 'success' : participant.status === 'left' ? 'warning' : 'secondary'}>
              {participant.status || 'Unknown'}
            </Badge>
          </div>
        </div>

        {/* Stats Cards Row — hidden when focused on pre-session data only */}
        {!preSessionOnly && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          {/* Sessions */}
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 flex items-center gap-3 sm:block">
            <div className="flex items-center justify-between sm:mb-2 min-w-[100px] sm:min-w-0">
              <span className="text-xs sm:text-xs text-neutral-500 font-medium">Sessions</span>
              <svg className="w-4 h-4 text-blue-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex items-center gap-2 sm:block">
              <p className="text-lg sm:text-2xl font-bold text-neutral-900">{overallStats.sessionsAttended}/{overallStats.totalSessions}</p>
              <p className={`text-xs sm:text-xs sm:mt-1 ${overallStats.sessionsAttended === overallStats.totalSessions ? 'text-green-600' : 'text-orange-600'}`}>
                {overallStats.totalSessions > 0 ? Math.round((overallStats.sessionsAttended / overallStats.totalSessions) * 100) : 0}% attendance
              </p>
            </div>
          </div>

          {/* Engagement Score */}
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 flex items-center gap-3 sm:block">
            <div className="flex items-center justify-between sm:mb-2 min-w-[100px] sm:min-w-0">
              <span className="text-xs sm:text-xs text-neutral-500 font-medium">Engagement</span>
              <svg className={`w-4 h-4 hidden sm:block ${overallStats.engagementScore >= 70 ? 'text-green-500' : overallStats.engagementScore >= 40 ? 'text-orange-500' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="flex items-center gap-2 sm:block">
              <p className={`text-lg sm:text-2xl font-bold ${overallStats.engagementScore >= 70 ? 'text-green-600' : overallStats.engagementScore >= 40 ? 'text-orange-600' : 'text-red-600'}`}>{overallStats.engagementScore}%</p>
              <p className="text-xs sm:text-xs text-neutral-500 sm:mt-1">Overall score</p>
            </div>
          </div>

          {/* Activities */}
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 flex items-center gap-3 sm:block">
            <div className="flex items-center justify-between sm:mb-2 min-w-[100px] sm:min-w-0">
              <span className="text-xs sm:text-xs text-neutral-500 font-medium">Activities</span>
              <svg className={`w-4 h-4 hidden sm:block ${completionRate >= 70 ? 'text-green-500' : completionRate >= 40 ? 'text-orange-500' : 'text-neutral-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex items-center gap-2 sm:block">
              <p className="text-lg sm:text-2xl font-bold text-neutral-900">{overallStats.activitiesCompleted}/{overallStats.launchedActivities}</p>
              <p className={`text-xs sm:text-xs sm:mt-1 ${completionRate >= 70 ? 'text-green-600' : completionRate >= 40 ? 'text-orange-600' : 'text-neutral-500'}`}>{completionRate}% completed</p>
            </div>
          </div>

          {/* Rating */}
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-4 flex items-center gap-3 sm:block">
            <div className="flex items-center justify-between sm:mb-2 min-w-[100px] sm:min-w-0">
              <span className="text-xs sm:text-xs text-neutral-500 font-medium">Rating Given</span>
              <svg className="w-4 h-4 text-yellow-500 hidden sm:block" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <div className="flex items-center gap-2 sm:block">
              <p className="text-lg sm:text-2xl font-bold text-neutral-900">{overallStats.sessionRating}/5</p>
              <p className="text-xs sm:text-xs text-neutral-500 sm:mt-1">Session rating</p>
            </div>
          </div>
        </div>
        )}

        {/* Performance Metrics Section */}
        {participantMetrics.hasData && (activeTab === 'overview' || activeTab === 'actionPlans') && (
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-5 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">Performance Metrics</h3>
                <p className="text-xs text-neutral-500">Participant learning indicators</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {/* ARS Gauge */}
              {participantMetrics.ars.value > 0 && (
                <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                  <GaugeMeter
                    value={participantMetrics.ars.value}
                    maxValue={1}
                    label="Action Readiness"
                    band={participantMetrics.ars.band?.label || 'Pending'}
                    color={participantMetrics.ars.band?.color || 'gray'}
                    size="sm"
                  />
                  {participantMetrics.ars.interpretation && (
                    <p className="mt-3 text-xs text-neutral-600 text-center">
                      {participantMetrics.ars.interpretation}
                    </p>
                  )}
                </div>
              )}

              {/* PCS Gauge */}
              {participantMetrics.pcs.value > 0 && (
                <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                  <GaugeMeter
                    value={participantMetrics.pcs.value}
                    maxValue={5}
                    label="Confidence Signal"
                    band={participantMetrics.pcs.band?.label || 'Pending'}
                    color={participantMetrics.pcs.band?.color || 'gray'}
                    size="sm"
                  />
                  {participantMetrics.pcs.interpretation && (
                    <p className="mt-3 text-xs text-neutral-600 text-center">
                      {participantMetrics.pcs.interpretation}
                    </p>
                  )}
                </div>
              )}

              {/* LII Gauge */}
              {participantMetrics.lii.value > 0 && (
                <div className="flex flex-col items-center p-4 bg-neutral-50 rounded-xl">
                  <GaugeMeter
                    value={participantMetrics.lii.value}
                    maxValue={5}
                    label="Implementation"
                    band={participantMetrics.lii.band?.label || 'Pending'}
                    color={participantMetrics.lii.band?.color || 'gray'}
                    size="sm"
                  />
                  {participantMetrics.lii.interpretation && (
                    <p className="mt-3 text-xs text-neutral-600 text-center">
                      {participantMetrics.lii.interpretation}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Session Participation */}
        {(activeTab === 'overview' || activeTab === 'participation') && (
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-5 mb-4 sm:mb-6">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3 sm:mb-4">Session Participation</h3>
            <div className="space-y-6">
              {sessionParticipation.map((session, idx) => (
                <div key={session.day} className={idx > 0 ? 'pt-6 border-t border-neutral-100' : ''}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">{session.topic}</span>
                  </div>

                  {/* Per-Subsession Metrics - Counts */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="bg-neutral-50 rounded-lg p-2 sm:p-3 flex items-center gap-3 sm:block sm:text-center">
                      <p className="text-xs sm:text-[10px] text-neutral-500 min-w-[80px] sm:min-w-0 sm:order-2">Activities</p>
                      <p className="text-base sm:text-lg font-bold text-primary-600 sm:order-1">{session.metrics.activitiesCount}</p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-2 sm:p-3 flex items-center gap-3 sm:block sm:text-center">
                      <p className="text-xs sm:text-[10px] text-neutral-500 min-w-[80px] sm:min-w-0 sm:order-2">Challenges</p>
                      <p className="text-base sm:text-lg font-bold text-orange-600 sm:order-1">{session.metrics.challengesCount}</p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-2 sm:p-3 flex items-center gap-3 sm:block sm:text-center">
                      <p className="text-xs sm:text-[10px] text-neutral-500 min-w-[80px] sm:min-w-0 sm:order-2">Action Plans</p>
                      <p className="text-base sm:text-lg font-bold text-green-600 sm:order-1">{session.metrics.actionPlansCount}</p>
                    </div>
                  </div>

                  {/* Per-Subsession Metrics - Indices */}
                  {(session.metrics.ars.value > 0 || session.metrics.pcs.value > 0 || session.metrics.lii.value > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
                      {session.metrics.ars.value > 0 && (
                        <div className="bg-purple-50 rounded-lg p-2 sm:p-3 flex items-center gap-3 sm:block sm:text-center">
                          <p className="text-xs text-neutral-500 min-w-[100px] sm:min-w-0 sm:order-2">Action Readiness</p>
                          <p className="text-base sm:text-lg font-bold text-purple-600 sm:order-1">{(session.metrics.ars.value * 100).toFixed(0)}%</p>
                          {session.metrics.ars.band && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700">
                              {session.metrics.ars.band.label}
                            </span>
                          )}
                        </div>
                      )}
                      {session.metrics.pcs.value > 0 && (
                        <div className="bg-blue-50 rounded-lg p-2 sm:p-3 flex items-center gap-3 sm:block sm:text-center">
                          <p className="text-xs text-neutral-500 min-w-[100px] sm:min-w-0 sm:order-2">Confidence</p>
                          <p className="text-base sm:text-lg font-bold text-blue-600 sm:order-1">{session.metrics.pcs.value.toFixed(1)}/5</p>
                          {session.metrics.pcs.band && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">
                              {session.metrics.pcs.band.label}
                            </span>
                          )}
                        </div>
                      )}
                      {session.metrics.lii.value > 0 && (
                        <div className="bg-teal-50 rounded-lg p-2 sm:p-3 flex items-center gap-3 sm:block sm:text-center">
                          <p className="text-xs text-neutral-500 min-w-[100px] sm:min-w-0 sm:order-2">Implementation</p>
                          <p className="text-base sm:text-lg font-bold text-teal-600 sm:order-1">{session.metrics.lii.value.toFixed(1)}/5</p>
                          {session.metrics.lii.band && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-teal-100 text-teal-700">
                              {session.metrics.lii.band.label}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Activities List */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                    {session.activities.map((activity, i) => {
                      let dotColor = 'bg-neutral-300';
                      let textColor = 'text-neutral-500';
                      let statusText = 'Not Launched';

                      if (activity.status === 'completed') {
                        dotColor = 'bg-green-500';
                        textColor = 'text-green-600';
                        statusText = 'Completed';
                      } else if (activity.status === 'not_attended') {
                        dotColor = 'bg-amber-400';
                        textColor = 'text-amber-600';
                        statusText = 'Missed';
                      }

                      return (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 bg-neutral-50 rounded-lg">
                          <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-neutral-900 truncate">{activity.name}</p>
                            <p className={`text-[10px] truncate ${textColor}`}>{statusText}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Key Takeaways */}
                  {session.keyTakeaways.length > 0 && (
                    <div className="bg-neutral-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-neutral-700 mb-2">Key Takeaways:</p>
                      <div className="flex flex-wrap gap-2">
                        {session.keyTakeaways.map((takeaway, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-1 bg-white border border-neutral-200 rounded text-xs text-neutral-700">
                            {takeaway}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Challenges & Action Plans */}
        {(activeTab === 'overview' || activeTab === 'challenges' || activeTab === 'actionPlans') && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* Challenges */}
            {(activeTab === 'overview' || activeTab === 'challenges') && (
              <div className="bg-white rounded-xl border border-neutral-200">
                <div className="p-3 sm:p-5 border-b border-neutral-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-red-100 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-semibold text-neutral-900">Challenges</h3>
                      <p className="text-[10px] sm:text-xs text-neutral-500">{personalChallenges.length} topics identified</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 sm:p-5 max-h-80 overflow-y-auto">
                  {personalChallenges.length > 0 ? (
                    <div className="space-y-4">
                      {personalChallenges.map((item, idx) => (
                        <div key={idx}>
                          <span className="text-xs font-semibold text-orange-600">Topic: {item.topic}</span>
                          <ul className="mt-1 space-y-1">
                            {item.challenges.map((challenge, i) => (
                              <li key={i} className="text-sm text-neutral-700 pl-3 border-l-2 border-neutral-200">
                                "{challenge}"
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 mx-auto text-neutral-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-neutral-500">No challenges recorded</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Plans */}
            {(activeTab === 'overview' || activeTab === 'actionPlans') && (
              <div className="bg-white rounded-xl border border-neutral-200">
                <div className="p-3 sm:p-5 border-b border-neutral-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-semibold text-neutral-900">Action Plans</h3>
                      <p className="text-[10px] sm:text-xs text-neutral-500">{overallStats.totalActionPlans} plans created</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 sm:p-5 max-h-80 overflow-y-auto">
                  {personalActionPlans.length > 0 ? (
                    <div className="space-y-4">
                      {personalActionPlans.map((item, idx) => (
                        <div key={idx}>
                          <span className="text-xs font-semibold text-orange-600">Topic: {item.topic}</span>
                          <ul className="mt-1 space-y-2">
                            {item.actions.map((action, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                                <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <div>
                                  <p>{typeof action === 'string' ? action : action.text}</p>
                                  {(action.byWhen || action.commitment) && (
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      {action.byWhen && (
                                        <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">
                                          By: {BY_WHEN_LABELS[action.byWhen] || action.byWhen}
                                        </span>
                                      )}
                                      {action.commitment && (
                                        <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">
                                          Commitment: {action.commitment}/10
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 mx-auto text-neutral-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-neutral-500">No action plans recorded</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* All Responses Tab */}
        {activeTab === 'responses' && (
          <div className="bg-white rounded-xl border border-neutral-200 p-3 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h3 className="text-sm font-semibold text-neutral-900">All Responses</h3>
              <div className="flex items-center gap-3">
                {/* Quick filter buttons for common types */}
                {/* <div className="hidden sm:flex flex-wrap gap-2">
                  <button
                    onClick={() => setResponseFilter('all')}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                      responseFilter === 'all'
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    All ({allResponses.length})
                  </button>
                  <button
                    onClick={() => setResponseFilter('poll')}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                      responseFilter === 'poll'
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    Polls
                  </button>
                  <button
                    onClick={() => setResponseFilter('feedback')}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                      responseFilter === 'feedback'
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    Feedback
                  </button>
                  <button
                    onClick={() => setResponseFilter('challenges')}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                      responseFilter === 'challenges'
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    Challenges
                  </button>
                  <button
                    onClick={() => setResponseFilter('actionplans')}
                    className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                      responseFilter === 'actionplans'
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    Action Plans
                  </button>
                </div> */}
                {/* Dropdown for all filter options */}
                <select
                  value={responseFilter}
                  onChange={(e) => setResponseFilter(e.target.value)}
                  className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All ({allResponses.length})</option>
                  <option value="poll">Polls</option>
                  <option value="quiz">Quiz</option>
                  <option value="wordcloud">Word Cloud</option>
                  <option value="qa">Open Question</option>
                  <option value="feedback">Feedback</option>
                  <option value="challenges">Challenges</option>
                  <option value="actionplans">Action Plans</option>
                </select>
              </div>
            </div>

            <div className="space-y-3">
              {filteredResponses.length > 0 ? (
                filteredResponses.map((r, idx) => {
                  const formattedResponse = formatResponse(r.response, r.activityType, r.activityConfig);
                  const isArray = Array.isArray(formattedResponse);

                  return (
                    <div key={idx} className="bg-neutral-50 rounded-lg p-3 sm:p-4">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <span className={`w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-lg flex items-center justify-center ${getActivityTypeColor(r.activityType)}`}>
                          {getActivityIcon(r.activityType, 'sm')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                            <span className="text-[10px] sm:text-xs font-semibold text-neutral-900 capitalize">
                              {r.activityType?.replace('_', ' ')}
                            </span>
                            <span className="text-[10px] sm:text-xs text-neutral-400">•</span>
                            <span className="text-[10px] sm:text-xs text-neutral-500 truncate">{r.subSessionName}</span>
                          </div>
                          {r.activityTitle && (
                            <p className="text-xs text-neutral-500 mb-2">{r.activityTitle}</p>
                          )}
                          <div className="text-sm text-neutral-700">
                            {isArray ? (
                              <ul className="space-y-1">
                                {formattedResponse.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-neutral-400">{i + 1}.</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>{formattedResponse}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-neutral-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-neutral-500">No responses found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pre-Session Tab */}
        {activeTab === 'preSession' && (
          <div className="space-y-4 sm:space-y-5">
            {participant?.preSessionData && (
              participant.preSessionData.materialViews?.length > 0 ||
              participant.preSessionData.selfAssessmentResponses?.length > 0 ||
              participant.preSessionData.challenges?.length > 0 ||
              participant.preSessionData.expectations?.length > 0
            ) ? (
              <>
                {/* Pre-Read Materials Card */}
                {participant.preSessionData.materialViews?.length > 0 && (
                  <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 text-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                      </div>
                      <h4 className="text-base font-bold text-neutral-900">Pre-Read Materials</h4>
                      <p className="text-xs text-neutral-500 mt-0.5">Training materials the participant reviewed before the session</p>
                    </div>
                    <div className="p-4 space-y-2">
                      {participant.preSessionData.materialViews.map((mv, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
                          <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-neutral-900 truncate">{mv.materialName || mv.materialId}</p>
                            <p className="text-xs text-neutral-500">{mv.viewedAt ? new Date(mv.viewedAt).toLocaleString() : 'Viewed'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Self-Assessment Card */}
                {participant.preSessionData.selfAssessmentResponses?.length > 0 && (
                  <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 text-center">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
                        </svg>
                      </div>
                      <h4 className="text-base font-bold text-neutral-900">Self-Assessment</h4>
                      <p className="text-xs text-neutral-500 mt-0.5">How the participant rated themselves before training</p>
                    </div>
                    <div className="p-4 space-y-3">
                      {participant.preSessionData.selfAssessmentResponses.map((sa, idx) => (
                        <div key={idx} className="p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                          <p className="text-sm font-semibold text-neutral-800 mb-2">{sa.questionText || `Question ${(sa.questionIndex || 0) + 1}`}</p>
                          {typeof sa.response === 'number' ? (
                            <div className="flex items-center gap-1.5">
                              {[1, 2, 3, 4, 5].map(v => (
                                <span key={v} className={`text-xl ${v <= sa.response ? '' : 'opacity-20'}`}>
                                  {['😞', '😑', '🙂', '😄', '🤩'][v - 1]}
                                </span>
                              ))}
                              <span className="text-sm font-bold text-neutral-700 ml-2">{sa.response}/5</span>
                            </div>
                          ) : (
                            <p className="text-sm text-neutral-600 bg-white p-2.5 rounded-lg border border-neutral-100">{sa.response}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Challenges Card */}
                {participant.preSessionData.challenges?.length > 0 && (
                  <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 text-center">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                        </svg>
                      </div>
                      <h4 className="text-base font-bold text-neutral-900">Challenges</h4>
                      <p className="text-xs text-neutral-500 mt-0.5">Real-world challenges the participant shared before the session</p>
                    </div>
                    <div className="p-4 space-y-2">
                      {participant.preSessionData.challenges.map((c, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                          <span className="w-6 h-6 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{idx + 1}</span>
                          <p className="text-sm text-neutral-800 leading-relaxed">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Expectations Card */}
                {participant.preSessionData.expectations?.length > 0 && (
                  <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 text-center">
                      <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                        </svg>
                      </div>
                      <h4 className="text-base font-bold text-neutral-900">Expectations</h4>
                      <p className="text-xs text-neutral-500 mt-0.5">What the participant hoped to gain from the training</p>
                    </div>
                    <div className="p-4 space-y-2">
                      {participant.preSessionData.expectations.map((e, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                          <span className="w-6 h-6 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{idx + 1}</span>
                          <p className="text-sm text-neutral-800 leading-relaxed">{e.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
                <svg className="w-16 h-16 mx-auto text-neutral-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
                <p className="text-base font-medium text-neutral-500">No pre-session data available</p>
                <p className="text-sm text-neutral-400 mt-1">Pre-session responses will appear here once submitted</p>
              </div>
            )}
          </div>
        )}

        {/* Post-Session Tab */}
        {activeTab === 'postSession' && (() => {
          const postData = participant?.postSessionData;
          const plans = postData?.actionPlans || [];
          const reflections = postData?.reflections || [];
          const hasData = plans.length > 0 || reflections.length > 0;
          const completed = plans.filter(p => p.status === 'completed').length;
          const inProg = plans.filter(p => p.status === 'in_progress').length;
          const blocked = plans.filter(p => p.status === 'blocked').length;
          const validated = plans.filter(p => p.managerValidated).length;
          const allNotes = plans.flatMap((p, idx) => (p.progressNotes || []).map(n => ({ ...n, planTitle: p.title || `Plan ${idx + 1}`, planIdx: idx })));
          const participantNotes = allNotes.filter(n => n.author !== 'manager');
          const managerComments = allNotes.filter(n => n.author === 'manager');
          const statusCfg = {
            not_started: { color: 'bg-neutral-100 text-neutral-700', bar: 'bg-neutral-300', icon: '📋' },
            in_progress: { color: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500', icon: '🔄' },
            completed: { color: 'bg-green-100 text-green-700', bar: 'bg-green-500', icon: '✅' },
            blocked: { color: 'bg-red-100 text-red-700', bar: 'bg-red-500', icon: '🔴' },
          };

          return (
            <div className="space-y-4 sm:space-y-5">
              {hasData ? (
                <>
                  {/* Manager Code */}
                  {postData?.managerCode && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wider">Manager Access Code</p>
                        <p className="text-base font-mono font-bold text-indigo-900">{postData.managerCode}</p>
                      </div>
                      <span className="text-xs text-indigo-500 text-right">Share with manager to access<br />the reinforcement portal</span>
                    </div>
                  )}

                  {/* Action Plans Card */}
                  {plans.length > 0 && (
                    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                      <div className="px-5 py-4 border-b border-neutral-100 text-center">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <h4 className="text-base font-bold text-neutral-900">Action Plans</h4>
                        <p className="text-xs text-neutral-500 mt-0.5">Specific actions the participant committed to implement after training</p>
                      </div>
                      <div className="p-4 space-y-4">
                        {plans.map((plan, idx) => {
                          const cfg = statusCfg[plan.status] || statusCfg.not_started;
                          return (
                            <div key={idx} className="rounded-xl border border-neutral-200 overflow-hidden">
                              <div className={`h-1.5 ${cfg.bar}`}></div>
                              <div className="p-4">
                                <div className="flex items-start justify-between gap-3 mb-3">
                                  <div className="flex-1">
                                    <p className="text-xs text-neutral-400 uppercase tracking-wider mb-1">Action Plan {idx + 1}</p>
                                    <h5 className="text-base font-bold text-neutral-900">{plan.title || 'Untitled Plan'}</h5>
                                    {plan.description && <p className="text-sm text-neutral-600 mt-1">{plan.description}</p>}
                                  </div>
                                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${cfg.color}`}>{cfg.icon} {(plan.status || 'not_started').replace(/_/g, ' ')}</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                  {plan.byWhen && (
                                    <div className="flex items-start gap-2.5 p-3 bg-blue-50 rounded-xl border border-blue-100">
                                      <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                      <div>
                                        <p className="text-xs text-blue-600 font-semibold">Target Timeline</p>
                                        <p className="text-sm font-bold text-neutral-900">{plan.byWhen.replace(/_/g, ' ')}</p>
                                      </div>
                                    </div>
                                  )}
                                  {plan.commitment != null && (
                                    <div className="flex items-start gap-2.5 p-3 bg-amber-50 rounded-xl border border-amber-100">
                                      <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>
                                      <div>
                                        <p className="text-xs text-amber-600 font-semibold">Commitment Level</p>
                                        <p className="text-sm font-bold text-neutral-900">{plan.commitment} out of 10</p>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="flex flex-wrap gap-2 text-xs">
                                  {plan.managerValidated && <span className="px-2.5 py-1 bg-green-50 text-green-700 rounded-lg font-medium border border-green-100">✅ Manager Validated</span>}
                                  {plan.successMetric && <span className="px-2.5 py-1 bg-neutral-50 text-neutral-600 rounded-lg font-medium border border-neutral-100">Success Metric: {plan.successMetric}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Progress Card */}
                  {plans.length > 0 && (
                    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                      <div className="px-5 py-4 border-b border-neutral-100 text-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
                        </div>
                        <h4 className="text-base font-bold text-neutral-900">Action Plan Progress</h4>
                        <p className="text-xs text-neutral-500 mt-0.5">How the participant is tracking against their action plan commitments</p>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
                          <div className="bg-neutral-50 rounded-lg p-2.5 text-center"><p className="text-lg font-bold text-neutral-900">{plans.length}</p><p className="text-[10px] text-neutral-500">Total</p></div>
                          <div className="bg-green-50 rounded-lg p-2.5 text-center"><p className="text-lg font-bold text-green-600">{completed}</p><p className="text-[10px] text-green-600">Completed</p></div>
                          <div className="bg-blue-50 rounded-lg p-2.5 text-center"><p className="text-lg font-bold text-blue-600">{inProg}</p><p className="text-[10px] text-blue-600">In Progress</p></div>
                          <div className="bg-red-50 rounded-lg p-2.5 text-center"><p className="text-lg font-bold text-red-600">{blocked}</p><p className="text-[10px] text-red-600">Blocked</p></div>
                          <div className="bg-purple-50 rounded-lg p-2.5 text-center"><p className="text-lg font-bold text-purple-600">{validated}</p><p className="text-[10px] text-purple-600">Validated</p></div>
                        </div>
                        {plans.map((plan, idx) => (
                          plan.statusHistory?.length > 0 && (
                            <div key={idx} className="mb-3 last:mb-0">
                              <p className="text-xs font-semibold text-neutral-700 mb-1.5">{plan.title || `Plan ${idx + 1}`}</p>
                              <div className="flex flex-wrap items-center gap-1">
                                {plan.statusHistory.slice(-5).map((sh, shIdx, arr) => {
                                  const stC = { not_started: 'bg-neutral-100 text-neutral-600', in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700', blocked: 'bg-red-100 text-red-700' }[sh.status] || 'bg-neutral-100 text-neutral-600';
                                  return (
                                    <span key={shIdx} className="flex items-center gap-1">
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${stC}`}>{(sh.status || '').replace(/_/g, ' ')}</span>
                                      <span className="text-[10px] text-neutral-400">{sh.changedAt ? new Date(sh.changedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
                                      {shIdx < arr.length - 1 && <span className="text-neutral-300 mx-0.5">→</span>}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes & Manager Comments */}
                  {allNotes.length > 0 && (
                    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                      <div className="px-5 py-4 border-b border-neutral-100 text-center">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>
                        </div>
                        <h4 className="text-base font-bold text-neutral-900">Notes & Manager Comments</h4>
                        <p className="text-xs text-neutral-500 mt-0.5">Progress updates from the participant and feedback from their manager</p>
                      </div>
                      <div className="p-4">
                        <div className="flex gap-3 mb-4 text-xs">
                          <span className="px-2.5 py-1 bg-neutral-100 rounded-full font-medium text-neutral-700">Participant Notes: {participantNotes.length}</span>
                          <span className="px-2.5 py-1 bg-purple-100 rounded-full font-medium text-purple-700">Manager Comments: {managerComments.length}</span>
                        </div>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {allNotes.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)).map((note, nIdx) => (
                            <div key={nIdx} className={`p-3 rounded-xl ${note.author === 'manager' ? 'bg-purple-50 border border-purple-100' : 'bg-neutral-50 border border-neutral-100'}`}>
                              <div className="flex justify-between items-center mb-1.5">
                                <span className="font-semibold text-sm flex items-center gap-2">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${note.author === 'manager' ? 'bg-purple-200 text-purple-700' : 'bg-neutral-200 text-neutral-700'}`}>
                                    {(note.authorName || (note.author === 'manager' ? 'M' : 'P'))[0]}
                                  </span>
                                  {note.authorName || (note.author === 'manager' ? 'Manager' : participant.name)}
                                  {note.author === 'manager' && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-[10px] font-medium">MANAGER</span>}
                                </span>
                                <span className="text-xs text-neutral-400">{note.createdAt ? new Date(note.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                              </div>
                              <p className="text-sm text-neutral-700 leading-relaxed">{note.text}</p>
                              <p className="text-[10px] text-neutral-400 mt-1">Re: {note.planTitle}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reflections Card */}
                  {reflections.length > 0 && (
                    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                      <div className="px-5 py-4 border-b border-neutral-100 text-center">
                        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /></svg>
                        </div>
                        <h4 className="text-base font-bold text-neutral-900">Reflection Cycles</h4>
                        <p className="text-xs text-neutral-500 mt-0.5">{(() => { const d = [...new Set((reflections || []).map((r) => Number(r.intervalDays)).filter(Number.isFinite))].sort((a, b) => a - b); if (!d.length) return 'Scheduled reflections after the session'; const list = d.length === 1 ? String(d[0]) : d.slice(0, -1).join(', ') + ' and ' + d[d.length - 1]; return 'Scheduled reflections at ' + list + ' days'; })()}</p>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {reflections.map((ref, idx) => {
                            const rc = { completed: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', icon: '✅' }, available: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', icon: '📝' }, missed: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: '❌' } }[ref.status] || { bg: 'bg-neutral-50', border: 'border-neutral-200', badge: 'bg-neutral-100 text-neutral-500', icon: '⏳' };
                            return (
                              <div key={idx} className={`${rc.bg} rounded-xl border ${rc.border} p-4`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-lg">{rc.icon}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${rc.badge}`}>{(ref.status || 'pending').replace(/_/g, ' ')}</span>
                                </div>
                                <p className="text-base font-bold text-neutral-900">{ref.intervalDays}-Day Reflection</p>
                                <p className="text-xs text-neutral-500 mt-0.5">{ref.availableAt ? `Available: ${new Date(ref.availableAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Pending'}</p>
                                {ref.status === 'completed' && ref.responses?.length > 0 && (
                                  <div className="mt-3 space-y-2">{ref.responses.map((r, rIdx) => (
                                    <div key={rIdx} className="p-2.5 bg-white rounded-lg"><p className="text-xs font-medium text-neutral-800">{r.questionText}</p><p className="text-xs text-neutral-600 mt-1">{r.answer}</p></div>
                                  ))}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
                  <svg className="w-16 h-16 mx-auto text-neutral-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1}><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>
                  <p className="text-base font-medium text-neutral-500">No post-session data available</p>
                  <p className="text-sm text-neutral-400 mt-1">Action plans and reflections will appear here after the session ends</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Micro Sims Tab */}
        {activeTab === 'microSims' && microSimReports.length > 0 && (
          <MicroSimParticipantTab reports={microSimReports} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParticipantDetailsPage;
